import React, { useRef, useState, useMemo, useCallback, useEffect, ReactNode } from 'react';
import { Upload, Spin, message } from 'antd';
import {
  getPresignedURLs,
  startInferenceBatch,
  startInferenceBatchByModelVersion,
  startMultiFlowInferenceBatch,
} from '../lib/api/aws-api';
import { addFilesToDataset, addInferenceFilesToDataset } from '../lib/api/dataset-api';
import { UploadChangeParam } from 'antd/lib/upload';
import { UploadFile, RcFile } from 'antd/lib/upload/interface';
import { Batch } from '../types';
import { Icon } from '@ant-design/compatible';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

type NoInferenceProps = {
  datasetVersion?: null | string;
  multiple: boolean;
  modelId: null | number;
  modelVersion?: null | string;
  customComponent?: ReactNode;
  noInference: true;
  isMultiFlow?: boolean;
  isSplitter?: boolean;
  multiRun?: boolean;
  onBatchUploaded: (batchCreationResponse: Batch) => any;
  getPresignedUrl?: string;
  getPresignedParams?: object;
  publicDemo?: boolean;
  ids?: string[];
};
type InferenceProps = {
  datasetVersion?: null | string;
  multiple: boolean;
  modelId: null | number;
  modelVersion?: null | string;
  customComponent?: ReactNode;
  noInference?: false;
  isMultiFlow?: boolean;
  isSplitter?: boolean;
  multiRun?: boolean;
  onBatchUploaded: (inferenceIds: string[]) => any;
  getPresignedUrl?: string;
  getPresignedParams?: object;
  publicDemo?: boolean;
  ids?: string[];
};

const BatchUploader = ({
  datasetVersion,
  noInference,
  multiple,
  modelVersion,
  modelId,
  customComponent,
  onBatchUploaded,
  getPresignedUrl,
  getPresignedParams,
  isMultiFlow,
  isSplitter,
  multiRun,
  publicDemo,
  ids,
}: NoInferenceProps | InferenceProps) => {
  const { t } = useTranslation();
  const [spinnerShowed, showSpinner] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [batchCreationResponse, setBatchCreationResponse] = useState<null | Batch>(null);
  const [showMessage, setShowMessage] = useState<boolean>(false);

  const signedUrls = useMemo(
    () =>
      batchCreationResponse
        ? batchCreationResponse.files.map(({ url, fileName, id }) => ({
            id,
            signedUrl: url,
            file: fileName,
          }))
        : [],
    [batchCreationResponse]
  );
  const uploadedFiles = useMemo(() => fileList.filter(file => file.status === 'done'), [fileList]);
  const allFilesUploaded = useMemo(
    () =>
      !!signedUrls.length &&
      signedUrls.every(({ file: fileName }) => uploadedFiles.some(file => file.name === fileName)),
    [signedUrls, uploadedFiles]
  );

  const beforeUploadPromise = useRef<null | Promise<any>>(null);

  const resetUploadState = () => {
    beforeUploadPromise.current = null;
    setFileList([]);
    setBatchCreationResponse(null);
  };

  const onUpload = ({ fileList: uploadedFileList }: UploadChangeParam<UploadFile<any>>) => {
    setFileList([...uploadedFileList]);
  };

  const handleUpload = async () => {
    if (!batchCreationResponse) return;
    if (datasetVersion) {
      showSpinner(true);
      if (modelId && modelId !== -1) {
        const response = await addInferenceFilesToDataset(datasetVersion, batchCreationResponse);
        await startInferenceBatch(modelId, batchCreationResponse);
        onBatchUploaded(response);
      } else {
        const response = await addFilesToDataset(datasetVersion, batchCreationResponse);
        onBatchUploaded(response);
      }
      showSpinner(false);
    } else if (!noInference) {
      showSpinner(true);
      console.log(modelId);
      if (modelId) {
        if (isMultiFlow) {
          const response = await startMultiFlowInferenceBatch(modelId, batchCreationResponse, publicDemo || false);
          onBatchUploaded(response.inferenceIds);
        } else {
          const response = await startInferenceBatch(modelId, batchCreationResponse);
          onBatchUploaded(response.inferenceIds);
        }
      } else if (modelVersion) {
        const response = await startInferenceBatchByModelVersion(modelVersion, batchCreationResponse);
        onBatchUploaded(response.inferenceIds);
      }
      showSpinner(false);
    } else if (batchCreationResponse && !datasetVersion) {
      const typedOnBatchUploaded = onBatchUploaded as (response: Batch) => any;
      typedOnBatchUploaded(batchCreationResponse);
    }

    resetUploadState();
  };

  const onFilesUploaded = useCallback(handleUpload, [
    onBatchUploaded,
    batchCreationResponse,
    modelId,
    noInference,
    modelVersion,
    datasetVersion,
  ]);

  useEffect(() => {
    if (allFilesUploaded) {
      onFilesUploaded();
    }
  }, [allFilesUploaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const getSignedURLs = async (file: RcFile, fileList: RcFile[]) => {
    if (beforeUploadPromise.current) {
      return beforeUploadPromise.current;
    }

    const signedUrlPromise = (async () => {
      const response: {
        batchId: string;
        files: { url: string; fileName: string; id: string }[];
        pdfcontent?: boolean;
      } = await getPresignedURLs(
        getPresignedUrl || '/aws/pre-signed-urls',
        {
          ...(getPresignedParams || {}),
          fileNames: fileList.map(file => file.name),
          multiRun: multiRun,
        },
        publicDemo || false
      );

      setBatchCreationResponse({ ...response, pdfcontent: !isSplitter });
      return true;
    })();

    beforeUploadPromise.current = signedUrlPromise;
    return signedUrlPromise;
  };

  const getSignedURL = (file: RcFile) => {
    const signedUrl = signedUrls.find(url => url.file === file.name);
    if (signedUrl) return signedUrl.signedUrl;
    return '';
  };

  const checkCountForPublicDemo = useCallback(
    fileList => {
      if (publicDemo && ids?.length + fileList.length > 8) {
        return false;
      } else {
        return true;
      }
    },
    [ids, publicDemo]
  );

  useEffect(() => {
    if (showMessage) {
      message.warning(t('general.upload-overflow', { count: 8 - (ids?.length || 0) }));
      setShowMessage(false);
    }
  }, [showMessage, ids, t]);

  return (
    <Spin spinning={spinnerShowed} delay={0}>
      {customComponent ? (
        <Upload
          method={'put'}
          multiple={multiple}
          action={getSignedURL}
          beforeUpload={getSignedURLs}
          onChange={onUpload}>
          {customComponent}
        </Upload>
      ) : (
        <Dragger
          accept={publicDemo ? '.pdf' : ''}
          method={'put'}
          multiple={multiple}
          action={getSignedURL}
          beforeUpload={(file, fileList) => {
            if (checkCountForPublicDemo(fileList)) {
              setShowMessage(false);
              return getSignedURLs(file, fileList);
            } else {
              setShowMessage(true);
            }
          }}
          onChange={info => {
            if (checkCountForPublicDemo(info.fileList)) {
              return onUpload(info);
            }
          }}
          fileList={fileList}>
          <p className="ant-upload-drag-icon">
            <Icon type="inbox" />
          </p>
          <p className="ant-upload-text">{t('general.upload')}</p>
        </Dragger>
      )}
    </Spin>
  );
};

export default BatchUploader;
